import React, { useState, useMemo } from 'react';
import { Modal, Table, Input, Button, Space } from 'antd';
import type { Category } from '@/types/models/tournament';

const genderLabels: Record<string, string> = {
  male: 'М',
  female: 'Ж',
  mixed: 'М/Ж',
};

interface AddCategoryToTournamentModalProps {
  open: boolean;
  onCancel: () => void;
  onAdd: (categoryIds: number[]) => void;
  allCategories: Category[];
  alreadySelectedIds: number[];
}

export const AddCategoryToTournamentModal: React.FC<AddCategoryToTournamentModalProps> = ({
  open,
  onCancel,
  onAdd,
  allCategories,
  alreadySelectedIds,
}) => {
  const [searchText, setSearchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const availableCategories = useMemo(
    () => allCategories.filter((c) => !alreadySelectedIds.includes(c.id)),
    [allCategories, alreadySelectedIds]
  );

  const filteredCategories = useMemo(() => {
    if (!searchText.trim()) return availableCategories;
    const q = searchText.toLowerCase().trim();
    return availableCategories.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        `${c.ageFrom}-${c.ageTo}`.includes(q) ||
        `${c.weightFrom}-${c.weightTo}`.includes(q) ||
        (c.gender === 'male' && q.includes('муж')) ||
        (c.gender === 'female' && q.includes('жен'))
    );
  }, [availableCategories, searchText]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
  };

  const handleAdd = () => {
    onAdd(selectedRowKeys as number[]);
    setSelectedRowKeys([]);
    setSearchText('');
    onCancel();
  };

  const handleCancel = () => {
    setSelectedRowKeys([]);
    setSearchText('');
    onCancel();
  };

  const columns = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      sorter: (a: Category, b: Category) => a.name.localeCompare(b.name),
    },
    {
      title: 'Пол',
      dataIndex: 'gender',
      key: 'gender',
      width: 56,
      align: 'center' as const,
      render: (gender: string) => genderLabels[gender] ?? gender,
    },
    {
      title: 'Возраст',
      key: 'age',
      width: 100,
      render: (_: unknown, record: Category) => `${record.ageFrom}-${record.ageTo} лет`,
    },
    {
      title: 'Вес',
      key: 'weight',
      width: 100,
      render: (_: unknown, record: Category) => {
        const from = record.weightFrom === 0 ? '—' : record.weightFrom;
        const to = record.weightTo === 0 ? '—' : record.weightTo;
        return `${from}-${to} кг`;
      },
    },
    {
      title: 'Пояса',
      dataIndex: 'beltLevels',
      key: 'beltLevels',
      ellipsis: true,
      render: (arr: string[] | undefined) =>
        Array.isArray(arr) && arr.length > 0 ? arr.join(', ') : '—',
    },
  ];

  return (
    <Modal
      title="Добавить категории в турнир"
      open={open}
      onCancel={handleCancel}
      width={700}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Отмена
        </Button>,
        <Button
          key="add"
          type="primary"
          onClick={handleAdd}
          disabled={selectedRowKeys.length === 0}
        >
          Добавить выбранные ({selectedRowKeys.length})
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Input
          placeholder="Поиск по названию, возрасту или весу..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          style={{ width: '100%' }}
        />
        <Table<Category>
          rowKey="id"
          size="small"
          bordered
          rowSelection={rowSelection}
          columns={columns}
          dataSource={filteredCategories}
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `Всего: ${t}` }}
          locale={{
            emptyText: alreadySelectedIds.length === allCategories.length
              ? 'Все категории уже добавлены в турнир'
              : searchText
                ? 'Ничего не найдено'
                : 'Нет доступных категорий',
          }}
        />
      </Space>
    </Modal>
  );
};
